import { noop } from 'lodash/fp'
import useHover from 'hooks/useHover'
import usePress from 'hooks/usePress'
import useActionGuard from 'hooks/useActionGuard'
import { getPostRoute } from 'helpers/routes'
import { MessageSquare } from 'lucide-react'
import { FC, ReactNode } from 'react'
import { cn, useIsMobile } from '@wellesley/ui'

import { feedsApi } from '../../../api'
import { CardAction } from '../CardAction'
import { FooterProps } from '../types'
import PopupContent, {
  MAX_AVATARS,
  POPUP_OPEN_DELAY,
  POPUP_CLOSE_DELAY,
} from '../Popup'

type TMessageButton = Pick<
  FooterProps,
  'feed' | 'isReply' | 'canComment' | 'isCommentForm'
> & {
  isDisabled: boolean
  isOpenComment: boolean
  readonly?: boolean
  customCommentContent?: ReactNode
  handler: () => void
}

const MessageButton: FC<TMessageButton> = ({
  feed,
  isReply,
  isDisabled,
  isOpenComment,
  canComment = true,
  isCommentForm = true,
  handler,
  readonly,
  customCommentContent,
}) => {
  const isMobile = useIsMobile()
  const commentsCount = feed.stats.comments || 0
  const hasComments = commentsCount > 0
  const [, isHovered, , handleMouseLeave, handleMouseEnter] =
    useHover<HTMLDivElement>({
      delay: POPUP_OPEN_DELAY,
      leaveDelay: POPUP_CLOSE_DELAY,
      useRefElement: false,
    })

  const [isPressed, touchEvents] = usePress()

  const { ActionTooltipWrapper } = useActionGuard()

  const canShowPopup = (isPressed || isHovered) && hasComments
  const canHandleComment = !readonly && isCommentForm && canComment

  const { data: activityData, isLoading: isLoadingComments } =
    feedsApi.useGetApiV1PostsByPostIdCommentersQuery(
      {
        postId: feed.id,
        limit: MAX_AVATARS,
      },
      { skip: !canShowPopup },
    )

  const commenters = activityData?.map(({ user }) => user) || []

  return (
    <ActionTooltipWrapper>
      <div
        className="relative"
        {...(isMobile
          ? touchEvents
          : {
              onMouseEnter: handleMouseEnter,
              onMouseLeave: handleMouseLeave,
            })}
      >
        {canShowPopup && (
          <PopupContent
            users={commenters}
            counter={commentsCount}
            isLoading={isLoadingComments}
            actionButtonLink={getPostRoute(feed.author, feed.id)}
          />
        )}
        <CardAction
          hideValue={Boolean(customCommentContent)}
          disabled={isDisabled}
          name="reply"
          value={isReply ? 'Reply' : commentsCount}
          handler={canHandleComment ? handler : noop}
          className={cn('h-full', {
            'text-blue-500': isReply && isOpenComment,
          })}
        >
          {customCommentContent || (
            <MessageSquare
              size={20}
              color={isOpenComment ? 'var(--blue-500)' : 'var(--gray-600)'}
            />
          )}
        </CardAction>
      </div>
    </ActionTooltipWrapper>
  )
}

export default MessageButton
